import { ReactNode, useId } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

export const DRAWER_WIDTH = { sm: 460, md: 820 } as const;

export type AppDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  context?: ReactNode;
  width?: "sm" | "md";
  actions?: ReactNode;
  footerLeft?: ReactNode;
  closeLabel?: string;
  children?: ReactNode;
};

// zIndex de modal: acima do topbar (E2E B-02 do login-rbac).
export default function AppDrawer({
  open,
  onClose,
  title,
  context,
  width = "sm",
  actions,
  footerLeft,
  closeLabel = "Fechar",
  children,
}: AppDrawerProps) {
  const titleId = useId();
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{
        sx: { width: { xs: "100%", sm: DRAWER_WIDTH[width] }, maxWidth: "100%", display: "flex", flexDirection: "column" },
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" id={titleId} sx={{ fontSize: 17, fontWeight: 700, color: "primary.main" }}>
            {title}
          </Typography>
          {context && <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{context}</Typography>}
        </Box>
        <IconButton aria-label={closeLabel} onClick={onClose} sx={{ mt: -0.5, mr: -1, color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>{children}</Box>
      {(actions || footerLeft) && (
        <Box
          data-testid="drawer-actions"
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{footerLeft}</Box>
          {actions}
        </Box>
      )}
    </Drawer>
  );
}
